// @ts-ignore
import React from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";

import { UNSAFE_media } from "@atlaskit/primitives/responsive";

const sectionCss = css`
    margin: 0 auto;
    max-width: 1200px;
    padding-bottom: 64px;
    padding-top: 64px;

    a {
        color: #0052cc;
        text-decoration: underline;

        &:hover {
            color: #0065ff;
            text-decoration: none;
        }
    }

    ${UNSAFE_media.below.sm} {
        padding-bottom: 0;
    }
`;

const headerCss = css`
    color: #253858;
    font-family: var(--bxp-charlie-display-font-family);
    font-size: 2.25rem;
    font-weight: 500;
    line-height: 1.33333333;
    margin: 0 auto;
    padding: 32px 16px 48px;
    text-align: center;
    width: 66.66666%;

    ${UNSAFE_media.below.sm} {
        font-size: 1.5rem;
        padding-top: 32px;
        width: 100%;
    }
`;

const cardsWrapperCss = css`
    align-items: stretch;
    display: flex;
    gap: 32px;
    padding: 0 16px;

    ${UNSAFE_media.below.sm} {
        flex-direction: column;
        margin: 0 auto;
        width: 66.66666%;
    }

    @media (max-width: 540px) {
        width: 100%;
    }
`;

const cardWrapperCss = css`
    align-items: center;
    background-color: #ffffff;
    border-color: #f4f5f7;
    box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    display: flex;
    flex: 1;
    flex-direction: column;
    line-height: 1.71428571;
    padding: 0 0 54px 0;
    text-align: center;
`;

const cardHeaderCss = (ctaEmphasis?: boolean) => css`
    background: ${ctaEmphasis ? "#f4f5f7" : "none"};
    border-bottom: 2px solid "#f4f5f7";
    font-size: 1.5rem;
    font-weight: 500;
    letter-spacing: 0.3px;
    line-height: 1.33333333;
    margin: 0;
    padding: 24px 12px;
    width: 100%;
`;

const costCss = css`
    font-family: var(--bxp-charlie-display-font-family);
    font-size: 44px;
    font-weight: 600;
    line-height: 1;
    margin-bottom: 12px;
    margin-top: 40px;
`;

const costLabelCss = css`
    color: #172b4d;
    font-size: 1.25rem;
    line-height: 1.2;
`;

const quantityCss = css`
    color: #172b4d;
    font-size: 0.875rem;
    line-height: 1.71428571;
`;

const ctaCss = (ctaEmphasis?: boolean) => css`
    background: ${ctaEmphasis ? "#0052cc" : "transparent"};
    border: 1px solid #0052cc;
    border-radius: 3px;
    color: ${ctaEmphasis ? "#ffffff" : "#0052cc"} !important;
    display: block;
    font-size: 16px;
    font-weight: ${ctaEmphasis ? 500 : 400};
    line-height: 1.5;
    margin-bottom: 24px;
    margin-top: 32px;
    padding: 0.4375rem 16px;
    text-decoration: none !important;
    transition: all 250ms;

    &:hover {
        background: ${ctaEmphasis ? "#0047b3" : "#ebecf0"};
        border-color: #0047b3;
    }
`;

const footerWrapperCss = css`
    margin: 32px 16px;
    padding: 0;

    p {
        margin: 0;
        padding: 40px;
    }
`;

type PricingTiersProps = {
    header: string;
    cards: PricingCardProps[];
    footer: ReactJSXElement;
};

export type PricingCardProps = {
    ctaEmphasis: boolean;
    tier: string;
    cost: string;
    costLabel: string;
    quantity: string;
    cta: string;
    ctaUrl: string;
    description1: string;
    description2: string;
    linkText?: string;
    linkUrl?: string;
};

const PricingCard = ({
    ctaEmphasis,
    tier,
    cost,
    costLabel,
    quantity,
    cta,
    ctaUrl,
    description1,
    description2,
    linkText,
    linkUrl
}: PricingCardProps) => {
    return (
        <div css={cardWrapperCss}>
            <h3 css={cardHeaderCss(ctaEmphasis)}>{tier}</h3>
            <div css={costCss}>{cost}</div>
            <div css={costLabelCss}>{costLabel}</div>
            <div css={quantityCss}>{quantity}</div>
            <a href={ctaUrl} css={ctaCss(ctaEmphasis)}>
                {cta}
            </a>
            <div>{description1}</div>
            <div>{description2}</div>
            {linkText && linkUrl && <a href={linkUrl}>{linkText}</a>}
        </div>
    );
};

export const PricingTiers = ({ header, cards, footer }: PricingTiersProps) => {
    return (
        <section css={sectionCss}>
            <h2 css={headerCss}>{header}</h2>
            <div css={cardsWrapperCss}>
                {cards.map((card, index) => (
                    <PricingCard key={`PricingCard-${index}`} {...card} />
                ))}
            </div>
            {footer && (
                <div css={[cardWrapperCss, footerWrapperCss]}>{footer}</div>
            )}
        </section>
    );
};

export default PricingTiers;
